/**
 * The consent gate: the owner's terms and the two ways out of them.
 *
 * Pure presentation. `accept` and `dismiss` are events; what accepting starts
 * is the panel's decision, made from the widget's modality.
 */

import { LitElement, css, html } from "lit";
import { customElement, property, query } from "lit/decorators.js";

@customElement("deepagent-consent")
export class DeepAgentConsent extends LitElement {
  static override styles = css`
    :host {
      display: block;
      padding: 14px 16px 16px;
      border-top: 1px solid var(--da-border);
      flex: 0 0 auto;
    }

    .terms-text {
      margin: 0 0 12px;
      font-size: 13px;
      line-height: 1.5;
      color: var(--da-text-muted);
      /* Long policies stay scannable instead of pushing the buttons off-panel. */
      max-height: 132px;
      overflow-y: auto;
      overscroll-behavior: contain;
    }

    .terms-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px;
    }

    button {
      appearance: none;
      cursor: pointer;
      height: 44px;
      padding: 0 12px;
      border-radius: var(--da-radius, 2px);
      font-family: inherit;
      font-size: 14px;
      font-weight: 600;
      transition:
        background-color 180ms cubic-bezier(0.16, 1, 0.3, 1),
        transform 180ms cubic-bezier(0.16, 1, 0.3, 1);
    }

    button:active {
      transform: scale(0.98);
      transition-duration: 90ms;
    }

    .terms-dismiss {
      border: 1px solid var(--da-input);
      background: transparent;
      color: var(--da-text);
    }

    @media (hover: hover) {
      .terms-dismiss:hover {
        background: var(--da-surface-2);
      }
    }

    .terms-accept {
      border: none;
      background: var(--da-accent);
      color: var(--da-accent-fg);
    }

    @media (hover: hover) {
      .terms-accept:hover {
        background: color-mix(in srgb, var(--da-accent) 88%, #000);
      }
    }

    button:focus-visible {
      outline: 2px solid var(--da-accent);
      outline-offset: 2px;
    }
  `;

  @property({ type: String }) content = "";
  @property({ type: String }) acceptLabel = "";
  @property({ type: String }) dismissLabel = "";

  @query(".terms-accept") private acceptButton!: HTMLButtonElement | null;

  /**
   * Accepting is what a visitor opened the panel to do, so focus lands there.
   *
   * Awaits this element's own render: the panel calls in right after its own,
   * one microtask before this shadow root has a button to focus.
   */
  async focusAccept(): Promise<void> {
    await this.updateComplete;
    this.acceptButton?.focus();
  }

  private emit(name: "accept" | "dismiss") {
    this.dispatchEvent(new CustomEvent(name, { bubbles: true, composed: true }));
  }

  override render() {
    return html`
      <p class="terms-text">${this.content}</p>
      <div class="terms-actions">
        <button type="button" class="terms-dismiss" @click=${() => this.emit("dismiss")}>
          ${this.dismissLabel}
        </button>
        <button type="button" class="terms-accept" @click=${() => this.emit("accept")}>
          ${this.acceptLabel}
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "deepagent-consent": DeepAgentConsent;
  }
}
